import tokens from '@kluein/design-tokens';
import { Check, Warning, WarningCircle, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { useCopyToClipboard } from 'hooks/use-copy-to-clipboard';
import { useNotifier, NotifierType } from 'hooks/use-notifier';
import { APP_BASEURL } from 'lib/urls';
import store from 'store';
import { useCreateCardScheduleMutation } from 'store/api/card-schedules/card-schedules.api';
import { useCreateCardMutation, useUpdateCardMutation } from 'store/api/cards';
import { useGetUserByIdQuery } from 'store/api/users/users.api';

import { calculateView } from '../utils';

import type { CardType, SearchResultCardType, UserType } from 'api/api.types';
import type { RootState, Dispatch } from 'store/store.types';

function useKlueCardMenu({
  cardId,
  profileId,
}: {
  cardId: number;
  profileId?: number;
}) {
  const { t } = useTranslation();
  const [copyToClipboard] = useCopyToClipboard();
  const { notify } = useNotifier();
  const [createCard] = useCreateCardMutation();
  const [updateCard] = useUpdateCardMutation();
  const [createCardSchedule] = useCreateCardScheduleMutation();
  const { data: klueAIUser } = useGetUserByIdQuery('klue_ai');
  const user = useSelector<RootState, UserType | null>(
    (state) => state.auth.user,
  );
  const dispatch = useDispatch<Dispatch>();
  const view = calculateView();

  // Try to get card from cards store first
  const cardFromCardsStore = useSelector<RootState, CardType | undefined>(
    (state) => state.cards.byId.get(String(cardId)),
  );

  // If not found, try to get from search results
  const cardFromSearchResults = useSelector<
    RootState,
    SearchResultCardType | undefined
  >((state) => state.search.results.find((result) => result.id === cardId));

  const card = cardFromCardsStore || cardFromSearchResults;

  const { sourceRivalId, sourceRivalName } = useSelector<
    RootState,
    { sourceRivalId: number | undefined; sourceRivalName: string | undefined }
  >((state) => {
    if (!card) return { sourceRivalId: undefined, sourceRivalName: undefined };
    let rivalId: number | undefined;
    if ('profileId' in card.board) {
      rivalId = state.profiles.byId.get(String(card.board.profileId))?.rivalId;
    } else if ('rival_id' in card.board) {
      rivalId = card.board.rival_id;
    }
    const rivalName = rivalId
      ? state.rivals.byId.get(String(rivalId))?.name
      : undefined;
    return { sourceRivalId: rivalId, sourceRivalName: rivalName };
  }, shallowEqual);

  const sortedCards = useSelector((state: RootState) =>
    store.select.cards.sortedCardsByBoardId(state, card?.board.id || 0),
  );

  const handleCopyEmbed = () => {
    copyToClipboard(
      `<iframe style="width: 100%; height: auto; aspect-ratio: 16 / 9; border: none;" src="${APP_BASEURL}/card/embed/${cardId}"></iframe>`,
    );
    notify({
      type: NotifierType.SUCCESS,
      message: t('Card:actions.copyEmbed.success'),
      icon: <Check size={tokens.iconSize.small} />,
    });
  };
  const handleCopyLink = () => {
    copyToClipboard(
      `${APP_BASEURL}/profile/${profileId}?viewCard=${cardId}&utm_source=card&utm_medium=web&utm_content=card-body`,
    );
    notify({
      type: NotifierType.SUCCESS,
      message: t('Card:actions.copyLink.success'),
      icon: <Check size={tokens.iconSize.small} />,
    });
  };
  const handleDuplicateCard = async () => {
    const handleError = () => {
      notify({
        type: NotifierType.ERROR,
        message: t('Card:actions.duplicateCard.error'),
        icon: <WarningCircle size={tokens.iconSize.small} />,
      });
    };

    // Duplicate only works with full CardType from cards store
    if (!cardFromCardsStore) {
      handleError();
      return;
    }
    const viewOrder = sortedCards.reduce((acc, curr, idx) => {
      if (curr.id === cardFromCardsStore.id && idx < sortedCards.length - 1) {
        const nextCard = sortedCards[idx + 1];
        return (
          (parseFloat(curr.viewOrder) + parseFloat(nextCard.viewOrder)) / 2
        );
      }
      return acc;
    }, parseFloat(cardFromCardsStore.viewOrder) + 0.5);

    try {
      const newCard = await createCard({
        data: {
          ...cardFromCardsStore.data,
          name: `${cardFromCardsStore.data.name} - ${t('Common:actions.copy')}`,
        },
        viewOrder,
        boardId: cardFromCardsStore.board.id,
        author: user?.id || 0,
        reviewerId: cardFromCardsStore?.reviewer?.id,
        templateName: cardFromCardsStore?.templateName,
        allAccess: cardFromCardsStore?.allAccess,
        visibilityGroups: cardFromCardsStore?.visibilityGroups.map(
          (group) => group.id,
        ),
        isDraft: cardFromCardsStore?.isDraft,
        tags: cardFromCardsStore?.tags.map((tag) => tag.id),
      }).unwrap();

      if (!newCard) {
        handleError();
      }

      dispatch.cards
        .addAndParseNewCard({
          newCard,
          boardCards: sortedCards,
        })
        .then(() => {
          notify({
            type: NotifierType.SUCCESS,
            message: t('Card:actions.duplicateCard.success'),
            icon: <Check size={tokens.iconSize.small} />,
          });

          setTimeout(() => {
            document
              .querySelector(`[data-card-id="${newCard.id}"]`)
              ?.parentElement?.scrollIntoView({
                behavior: 'smooth',
              });
          }, 100);
        });
    } catch (error) {
      handleError();
    }
  };
  const handleMoveCard = async ({ boardId }: { boardId: number }) => {
    try {
      // Move only works with full CardType from cards store
      if (!cardFromCardsStore) {
        throw new Error('Card not found');
      }

      const [updatedCard] = await updateCard({
        cardIds: [cardFromCardsStore.id],
        viewOrder: 0.0, // TODO: Give users option to move to beginning or end of lane?
        boardId: boardId,
      }).unwrap();

      if (!updatedCard) {
        throw new Error('Failed to update card');
      }

      dispatch.cards
        .updateAndParseCardsData({ updatedCards: [updatedCard] })
        .then((cards) => {
          dispatch.boards.moveCardToBoard({
            cardId: cardFromCardsStore.id,
            toBoardId: boardId,
          });
          notify({
            type: NotifierType.SUCCESS,
            message: t('Card:actions.moveCard.success'),
            icon: <Warning size={tokens.iconSize.small} />,
          });
          setTimeout(() => {
            document
              .querySelector(`[data-card-id="${updatedCard.id}"]`)
              ?.parentElement?.scrollIntoView({
                behavior: 'smooth',
              });
          }, 100);
        });
    } catch (error) {
      notify({
        type: NotifierType.ERROR,
        message: t('Card:actions.moveCard.error'),
        icon: <X size={tokens.iconSize.small} />,
      });
    }
  };

  const handleCopyToBoards = async (
    targets: {
      rivalId: number;
      boardId: number;
      rivalName: string;
      existingCardId?: number;
    }[],
    overrides?: {
      isDraft: boolean;
      allAccess: boolean;
      visibilityGroups?: number[];
      frequencyDays: number | null;
      researchMode: boolean;
      prompt?: string;
    },
  ): Promise<{ rivalId: number; cardId?: number }[]> => {
    if (!cardFromCardsStore) {
      notify({
        type: NotifierType.ERROR,
        message: t('Card:actions.copyToBoards.error', {
          count: targets.length,
        }),
        icon: <X size={tokens.iconSize.small} />,
      });
      return targets.map((target) => ({ rivalId: target.rivalId }));
    }

    const sourceSchedule = cardFromCardsStore.cardSchedule;
    const rivalName = sourceRivalName ?? '';

    const results = await Promise.allSettled(
      targets.map(async (target) => {
        const replaceRivalName = (text: string): string =>
          rivalName ? text.replaceAll(rivalName, target.rivalName) : text;

        let cardId = target.existingCardId;

        if (!cardId) {
          const cardTitle = replaceRivalName(
            cardFromCardsStore.data.name ?? '',
          );

          const newCard = await createCard({
            data: { name: cardTitle },
            viewOrder: 0,
            boardId: target.boardId,
            author: sourceSchedule
              ? klueAIUser?.id ?? user?.id ?? 0
              : user?.id ?? 0,
            reviewerId: cardFromCardsStore.reviewer?.id,
            templateName: cardFromCardsStore.templateName,
            allAccess: overrides?.allAccess ?? cardFromCardsStore.allAccess,
            visibilityGroups:
              overrides?.visibilityGroups ??
              cardFromCardsStore.visibilityGroups.map((group) => group.id),
            isDraft: overrides?.isDraft ?? cardFromCardsStore.isDraft,
            tags: cardFromCardsStore.tags.map((tag) => tag.id),
          }).unwrap();

          cardId = newCard?.id;
        }

        if (cardId && sourceSchedule) {
          try {
            await createCardSchedule({
              cardId,
              payload: {
                prompt: overrides?.prompt
                  ? replaceRivalName(overrides.prompt)
                  : replaceRivalName(sourceSchedule.prompt),
                researchMode:
                  overrides?.researchMode ?? sourceSchedule.researchMode,
                disabledSourceTypes: sourceSchedule.disabledSourceTypes,
                frequencyDays:
                  overrides?.frequencyDays ?? sourceSchedule.frequencyDays,
              },
            }).unwrap();
          } catch (error) {
            throw { cardId, cause: error };
          }
        }

        return { cardId };
      }),
    );

    const failures: { rivalId: number; cardId?: number }[] = [];
    let succeededCount = 0;

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        succeededCount++;
      } else {
        const reason = result.reason as { cardId?: number } | undefined;
        failures.push({
          rivalId: targets[index].rivalId,
          cardId: reason?.cardId ?? targets[index].existingCardId,
        });
      }
    });

    if (succeededCount > 0) {
      notify({
        type: NotifierType.SUCCESS,
        message: t('Card:actions.copyToBoards.success', {
          count: succeededCount,
        }),
        icon: <Check size={tokens.iconSize.small} />,
      });
    }
    if (failures.length > 0) {
      notify({
        type: NotifierType.ERROR,
        message: t('Card:actions.copyToBoards.error', {
          count: failures.length,
        }),
        icon: <X size={tokens.iconSize.small} />,
      });
    }

    return failures;
  };

  return {
    view,
    card,
    cardFromCardsStore,
    handleCopyEmbed,
    handleCopyLink,
    handleCopyToBoards,
    handleDuplicateCard,
    handleMoveCard,
    sortedCards,
    sourceRivalId,
    sourceRivalName,
  };
}

export default useKlueCardMenu;
